import { useMemo } from 'react';
import { ColumnsAddQuoteLine, QuoteLine } from 'shared/types/quoteline';
import { Column, useTable, useSortBy } from 'react-table';
import {
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  SxProps,
  Theme,
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  KeyboardArrowDown as ChevronDown,
  KeyboardArrowUp as ChevronUp,
} from '@mui/icons-material';
import { EditableTextField } from 'shared/components/EditableTextField';
import { DollarCell } from 'shared/tables/DollarCell';

const sortIconStyles: SxProps<Theme> = { verticalAlign: 'middle' };
const hiddenStyles: SxProps<Theme> = { visibility: 'hidden' };

interface ProductSelectionTableProps {
  lines: QuoteLine[];
  onChangeLine: (lineNumber: number, quantity: number) => void;
  onDeleteLine: (lineNumber: number) => void;
}

const getExtendedPrice = (list: string, qty: number) => {
  const floatList = parseFloat(list);
  if (isNaN(floatList) || isNaN(qty)) {
    return 0;
  }

  return floatList * qty;
};
export const ProductSelectionTable = ({ lines, onChangeLine, onDeleteLine }: ProductSelectionTableProps) => {
  const data = useMemo(() => {
    return lines.map(l => ({
      ...l,
      quantity: l.quantity,
      productNumber: l.product.item_number,
      productDescription: l.product.product_description,
      listPrice: parseFloat(l.product.list_price) || 0,
      extendedPrice: getExtendedPrice(l.product.list_price, l.quantity),
    }));
  }, [lines]);

  const columns = useMemo<Column<ColumnsAddQuoteLine>[]>(
    () => [
      {
        Header: 'Item Number',
        accessor: 'productNumber',
      },
      {
        Header: 'Product Description',
        accessor: 'productDescription',
      },
      {
        Header: 'Quantity',
        accessor: 'quantity',
        Cell: ({ row: { original } }) => (
          <EditableTextField
            initialValue={original.quantity ? original.quantity.toString() : ''}
            onUpdate={(newQty: number) => {
              onChangeLine(original.lineNumber, newQty);
            }}
            type='int'
          />
        ),
      },
      {
        Header: 'List Price',
        accessor: 'listPrice',
        Cell: ({ row: { original } }) => <DollarCell value={original.listPrice} />,
      },
      {
        Header: 'Ext. Price',
        accessor: 'extendedPrice',
        Cell: ({ row: { original } }) => <DollarCell value={original.extendedPrice} />,
      },
    ],
    [onChangeLine]
  );

  const tableInstance = useTable({ columns, data }, useSortBy);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  return (
    <TableContainer>
      <Table {...getTableProps()}>
        <TableHead>
          {headerGroups.map(headerGroup => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <TableCell {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {column.isSorted ? (
                    column.isSortedDesc ? (
                      <ChevronDown color='secondary' sx={sortIconStyles} />
                    ) : (
                      <ChevronUp color='secondary' sx={sortIconStyles} />
                    )
                  ) : (
                    <ChevronDown color='secondary' sx={{ ...sortIconStyles, ...hiddenStyles }} />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <TableRow {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>;
                })}
                <TableCell>
                  <IconButton
                    title='Remove line'
                    onClick={() => {
                      onDeleteLine(row.original.lineNumber);
                    }}
                    size='large'
                  >
                    <DeleteIcon color='error' />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
